import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./RuleRing.module.css";

type Rule = {
  id: string;
  label: string;
  code: string;
  colors: string[];
};

const RULES: Rule[] = [
  {
    id: "custom",
    label: "custom",
    code: 'custom: "#7C5CFF"',
    colors: ["var(--rule-custom)", "var(--rule-custom)"],
  },
  {
    id: "nitro",
    label: "useNitroTheme",
    code: "useNitroTheme: true",
    colors: ["var(--rule-nitro-1)", "var(--rule-nitro-2)", "var(--rule-nitro-3)"],
  },
  {
    id: "role",
    label: "useRoleColor",
    code: "useRoleColor: true",
    colors: ["var(--rule-role)", "var(--rule-role)"],
  },
  {
    id: "fallback",
    label: "fallback",
    code: "fallback: THEMES.slate",
    colors: ["var(--rule-fallback)", "var(--rule-fallback)"],
  },
];

/**
 * Live illustration of resolveCardColors()'s priority chain. Steps through
 * the four resolution rules automatically; clicking a step pins it. This
 * is the same order the library checks at runtime: custom > nitro > role > fallback.
 */
export default function RuleRing() {
  const [active, setActive] = useState(0);
  const [pinned, setPinned] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    if (pinned) return;
    const id = setInterval(() => {
      setActive((i) => (i + 1) % RULES.length);
    }, 2600);
    return () => clearInterval(id);
  }, [pinned]);

  const rule = RULES[active];
  const gradient =
    rule.colors.length > 1
      ? `linear-gradient(135deg, ${rule.colors.join(", ")})`
      : rule.colors[0];

  return (
    <div className={styles.wrap}>
      <div className={styles.card} style={{ ["--ring" as string]: gradient }}>
        <div className={styles.cardInner}>
          <span className={styles.eyebrow}>resolveCardColors()</span>
          <code className={styles.code}>{rule.code}</code>
        </div>
      </div>

      {!pinned && (
        <div className={styles.progressTrack}>
          <div key={active} className={styles.progressFill} />
        </div>
      )}

      <div className={styles.steps} role="tablist" aria-label={t("ruleRing.ariaLabel")}>
        {RULES.map((r, i) => (
          <button
            key={r.id}
            role="tab"
            aria-selected={i === active}
            className={`${styles.step} ${i === active ? styles.stepActive : ""}`}
            onClick={() => {
              setActive(i);
              setPinned(true);
            }}
          >
            <span className={styles.stepIndex}>{i + 1}</span>
            {r.label}
          </button>
        ))}
      </div>
    </div>
  );
}
